// ============================================
// Revenue by Bundle Tab
// Bundle breakdown table with proportional bars
// ============================================

import { useMemo } from 'react';
import type { BundleBreakdown } from './useFinancialData';

interface RevenueByBundleTabProps {
  data: BundleBreakdown[];
}

function formatCurrency(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  });
}

export function RevenueByBundleTab({ data }: RevenueByBundleTabProps) {
  // Already sorted by revenue descending from the hook, but ensure it
  const sorted = useMemo(
    () => [...data].sort((a, b) => b.revenueCents - a.revenueCents),
    [data],
  );

  const maxRevenue = useMemo(
    () => Math.max(...sorted.map((b) => b.revenueCents), 1),
    [sorted],
  );

  return (
    <div
      className="anim-fade-in-up bg-bg-card border border-bg-tertiary rounded-xl p-5"
    >
      <h3 className="text-lg font-display text-text-primary mb-4">
        Revenue by Bundle
      </h3>

      {sorted.length === 0 ? (
        <p className="text-text-muted text-base py-6 text-center">
          No bundle data available.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-bg-tertiary">
                <th className="py-2 pr-4 text-sm font-medium text-text-muted uppercase tracking-wider">
                  Bundle
                </th>
                <th className="py-2 pr-4 text-sm font-medium text-text-muted uppercase tracking-wider text-right">
                  Orders
                </th>
                <th className="py-2 pr-4 text-sm font-medium text-text-muted uppercase tracking-wider text-right">
                  Revenue
                </th>
                <th className="py-2 pr-4 text-sm font-medium text-text-muted uppercase tracking-wider text-right">
                  % Total
                </th>
                <th className="py-2 text-sm font-medium text-text-muted uppercase tracking-wider hidden sm:table-cell">
                  &nbsp;
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((bundle) => {
                const barPct = (bundle.revenueCents / maxRevenue) * 100;
                return (
                  <tr
                    key={bundle.bundleName}
                    className="border-b border-bg-tertiary/50 last:border-0"
                  >
                    <td className="py-3 pr-4 text-base text-text-primary font-medium">
                      {bundle.bundleName}
                    </td>
                    <td className="py-3 pr-4 text-base text-text-secondary text-right tabular-nums">
                      {bundle.orderCount}
                    </td>
                    <td className="py-3 pr-4 text-base text-text-primary font-medium text-right tabular-nums whitespace-nowrap">
                      {formatCurrency(bundle.revenueCents)}
                    </td>
                    <td className="py-3 pr-4 text-base text-text-muted text-right tabular-nums">
                      {bundle.pctOfTotal}%
                    </td>
                    <td className="py-3 hidden sm:table-cell w-1/4">
                      <div className="w-full bg-bg-elevated rounded h-2.5">
                        <div
                          className="bg-gold rounded h-2.5 transition-all duration-500"
                          style={{ width: `${Math.max(barPct, 2)}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
